/**
 * AreaShortDescriptionSection — shared editorial section for area short description.
 * Canonical owner: Editor portal. Reused by Admin portal.
 */
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { EditorialAiAssistant } from "@/components/ai/EditorialAiAssistant";
import type { UseFormReturn } from "react-hook-form";

interface LinkedPlace {
  place_name: string;
  place_status: string;
}

interface AreaShortDescriptionSectionProps {
  form: UseFormReturn<any>;
  areaName: string;
  parentAreaName?: string;
  areaType?: string;
  landskap?: string | null;
  municipality?: string | null;
  linkedPlaces?: LinkedPlace[];
  disabled?: boolean;
  hideAi?: boolean;
}

export function AreaShortDescriptionSection({
  form,
  areaName,
  parentAreaName,
  areaType,
  landskap,
  municipality,
  linkedPlaces = [],
  disabled = false,
  hideAi = false,
}: AreaShortDescriptionSectionProps) {
  return (
    <FormField
      control={form.control}
      name="description_short"
      render={({ field }) => {
        const hasText = (field.value || "").trim().length > 0;
        return (
          <FormItem>
            <div className="flex items-center justify-between">
              <FormLabel className="text-xs">Kort beskrivning</FormLabel>
              {!hideAi && (
                <EditorialAiAssistant
                  task="area_short_description_draft"
                  buildContext={() => {
                    const nearbyPlaceNames = linkedPlaces
                      .filter((lp) => lp.place_status === "published")
                      .slice(0, 8)
                      .map((lp) => lp.place_name);
                    return {
                      name: areaName,
                      parentAreaName: parentAreaName || undefined,
                      areaType: areaType || undefined,
                      landskap: landskap || undefined,
                      municipality: municipality || undefined,
                      nearbyPlaceNames:
                        nearbyPlaceNames.length > 0 ? nearbyPlaceNames : undefined,
                      currentText: field.value || undefined,
                      fieldType: "short_description",
                    };
                  }}
                  onAccept={(text) =>
                    form.setValue("description_short", text, { shouldDirty: true })
                  }
                  disabled={disabled || !areaName}
                  allowRewrite
                  hasExistingText={hasText}
                  rewriteContextOverride={{ fieldType: "short_description" }}
                  showAdvanced
                  lengthProfile="summary"
                  currentText={field.value || undefined}
                />
              )}
            </div>
            <FormControl>
              <Textarea
                placeholder="En kort, redaktionell sammanfattning av området."
                {...field}
                className="min-h-[72px] resize-none"
              />
            </FormControl>
            <FormDescription className="text-[11px] text-muted-foreground/60">
              {(field.value || "").length} tecken
            </FormDescription>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
